import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { APP_NAME } from '@/config/constants'
import {
  LayoutDashboard,
  Activity,
  Menu,
  Forklift,
  FileText,
  LogOut,
  type LucideIcon,
} from 'lucide-react'

const navigation: { name: string; href: string; icon: LucideIcon }[] = [
  { name: 'ダッシュボード', href: '/dashboard', icon: LayoutDashboard },
  { name: 'フォークリフト', href: '/forklifts', icon: Forklift },
  { name: 'メトリクス', href: '/analytics/metrics', icon: Activity },
  { name: 'ログ', href: '/logs', icon: FileText },
]

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, signOut } = useAuth()
  const handleLogout = async () => {
    await signOut()
  }
  const location = useLocation()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー（最上部） */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {/* モバイルメニューボタン */}
              <button
                className="lg:hidden p-2 rounded-md hover:bg-gray-100 flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <Activity
                className="h-6 w-6 flex-shrink-0"
                style={{ color: '#3b82f6' }}
              />
              <span className="text-lg font-semibold text-gray-900 whitespace-nowrap truncate">
                {APP_NAME}
              </span>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="text-sm text-gray-600 hidden md:inline truncate max-w-[16rem]">
                {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="ログアウト"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">ログアウト</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* モバイルサイドバーオーバーレイ */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex">
        {/* サイドバー（アイコンのみ、ヘッダーの下） */}
        <aside
          className={`fixed top-16 bottom-0 left-0 z-50 w-64 lg:w-16 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 lg:shadow-none lg:border-r lg:border-t lg:border-gray-200`}
        >
          <nav className="mt-4 px-2">
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  } lg:justify-center lg:px-2`}
                  onClick={() => setSidebarOpen(false)}
                  title={item.name}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium lg:hidden">{item.name}</span>
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* ページコンテンツ */}
        <main className="flex-1 lg:ml-16 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
